import { useLazyPostChronologyEntryDeleteQuery, useLazyPostTimelineEntryDeleteQuery } from '@/api';
import {
  selectCaseIdData,
  selectSharedDataActiveTimelineChronologyType,
  TimelineChronologyType,
  useAppSelector,
} from '@/store';
import { BROADCAST_CHRONOLOGY_CHANGED_BUNDLE_REFRESH, useFletchersBroadcast } from '@/broadcast';
import { confirmDialog } from '@repo/atomic-components';
import { useCallback } from 'react';

export const useTimelineChronologyDelete = () => {
  const activeTimelineChronologyType = useAppSelector(selectSharedDataActiveTimelineChronologyType);
  const caseId = useAppSelector(selectCaseIdData);
  const { sendMessageWithoutResponse } = useFletchersBroadcast();

  const [triggerPostTimelineEntryDelete] = useLazyPostTimelineEntryDeleteQuery();
  const [triggerPostChronologyEntryDelete] = useLazyPostChronologyEntryDeleteQuery();

  const handleTimelineChronologyDelete = useCallback(
    async (confirmDialogId: string, entryIds: string[]) => {
      const confirmed = await confirmDialog({
        id: confirmDialogId,
        entryIds: entryIds,
      });

      if (confirmed) {
        if (activeTimelineChronologyType === TimelineChronologyType.TIMELINE && caseId && entryIds.length) {
          triggerPostTimelineEntryDelete({
            caseId: caseId,
            entryIds: entryIds,
          });
        }
        if (activeTimelineChronologyType === TimelineChronologyType.CHRONOLOGY && caseId && entryIds.length) {
          triggerPostChronologyEntryDelete({
            caseId: caseId,
            entryIds: entryIds,
          })
            .unwrap()
            .then(() => {
              // Tell the Bundle (Full/Light) tabs that these chronology entries
              // are gone so they can clear the "in chronology" thumbnail flag.
              sendMessageWithoutResponse(BROADCAST_CHRONOLOGY_CHANGED_BUNDLE_REFRESH, { entryIds });
            })
            .catch(() => {
              // Delete failure is already surfaced by the API layer; nothing to
              // broadcast since the bundle state hasn't changed.
            });
        }
      }
    },
    [
      activeTimelineChronologyType,
      caseId,
      sendMessageWithoutResponse,
      triggerPostChronologyEntryDelete,
      triggerPostTimelineEntryDelete,
    ],
  );

  return { handleTimelineChronologyDelete };
};
